import React from 'react';
import { Stack, Group, Text, Button, ActionIcon, Checkbox, Select } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconPlus, IconTrash } from '@tabler/icons-react';

export interface SpecialHourPeriod {
  date: Date;
  openTime: string;
  closeTime: string;
  closed: boolean;
}

interface SpecialHoursFormProps {
  value: SpecialHourPeriod[];
  onChange: (val: SpecialHourPeriod[]) => void;
}

export function SpecialHoursForm({ value, onChange }: SpecialHoursFormProps) {
  const addPeriod = () => {
    onChange([
      ...value,
      { date: new Date(), openTime: '09:00', closeTime: '17:00', closed: false },
    ]);
  };

  const removePeriod = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const updatePeriod = (index: number, key: keyof SpecialHourPeriod, val: unknown) => {
    const updated = [...value];
    const existing = updated[index];
    if (!existing) return;
    const item = { ...existing };
    if (key === 'date') item.date = val as Date;
    else if (key === 'openTime') item.openTime = val as string;
    else if (key === 'closeTime') item.closeTime = val as string;
    else if (key === 'closed') item.closed = val as boolean;
    updated[index] = item;
    onChange(updated);
  };

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text fw={600} size="sm">
          Special & Holiday Hours
        </Text>
        <Button
          variant="outline"
          color="red"
          size="xs"
          leftSection={<IconPlus size={14} />}
          onClick={addPeriod}
        >
          Add Holiday Exception
        </Button>
      </Group>

      {value.length === 0 ? (
        <Text size="xs" c="dimmed" fs="italic">
          No special holiday hours scheduled yet.
        </Text>
      ) : (
        <Stack gap="xs">
          {value.map((period, idx) => (
            <Group key={idx} align="flex-end" gap="xs">
              <DateInput
                label="Date"
                placeholder="Pick date"
                value={period.date}
                onChange={(val) => updatePeriod(idx, 'date', val || new Date())}
                size="xs"
                required
                style={{ width: 140 }}
              />
              <Checkbox
                label="Closed all day"
                checked={period.closed}
                onChange={(e) => updatePeriod(idx, 'closed', e.currentTarget.checked)}
                mb="xs"
              />
              {!period.closed && (
                <>
                  <Select
                    label="Open"
                    size="xs"
                    data={Array.from({ length: 24 }).map((_, i) => {
                      const hour = String(i).padStart(2, '0');
                      return { value: `${hour}:00`, label: `${hour}:00` };
                    })}
                    value={period.openTime}
                    onChange={(val) => updatePeriod(idx, 'openTime', val)}
                    style={{ width: 90 }}
                  />
                  <Select
                    label="Close"
                    size="xs"
                    data={Array.from({ length: 24 }).map((_, i) => {
                      const hour = String(i).padStart(2, '0');
                      return { value: `${hour}:00`, label: `${hour}:00` };
                    })}
                    value={period.closeTime}
                    onChange={(val) => updatePeriod(idx, 'closeTime', val)}
                    style={{ width: 90 }}
                  />
                </>
              )}
              <ActionIcon
                color="red"
                variant="light"
                size="lg"
                onClick={() => removePeriod(idx)}
                mb={2}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
